<template>
  <Card class="mt-4 px-2" pt:body:class="p-3!" pt:content:class="">
    <template #header>
      <div v-if="errors.length" class="flex flex-col gap-2">
        <Message severity="error" :closeable="false">
          <div class="flex flex-col gap-1">
            <p v-for="error in errors">{{ error }}</p>
          </div>
        </Message>
      </div>
    </template>
    <template #content>
      <div class="flex flex-col gap-2">
        <form @submit="onSubmit">
          <StackedLabelInput name="descriptor" label="Field Descriptor" />
          <StackedLabelKeyValueDropdown name="scope" label="Field Scope" :type="ScopeDropdownOptions" />
          <StackedLabelKeyValueDropdown name="dataType" label="Data Type" :type="DataTypeDropdownOptions" />
          <div v-show="values.dataType === CustomFieldDataType.NUMERIC" class="flex flex-row gap-2">
            <StackedLabelKeyValueDropdown name="numericFormat" label="Number Format" :type="NumericFormatDropdownOptions" size="basis-1/2" />
            <StackedLabelInputNumber name="precision" label="Decimal Places" size="basis-1/2" :show-buttons="true" :min="0" :max="10" />
          </div>
          <StackedLabelChips v-show="values.dataType === CustomFieldDataType.LIST" name="listValues" label="List Values" />
          <Button type="submit" label="Submit" icon="pi pi-check" iconPos="right" class="mt-4" />
        </form>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { z } from "zod";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { CustomFieldScope, ScopeDropdownOptions } from "@/enums/CustomFieldScope";
import { CustomFieldDataType, DataTypeDropdownOptions } from "@/enums/CustomFieldDataType";
import { CustomFieldNumericOption, NumericFormatDropdownOptions } from "@/enums/CustomFieldNumericOption";

const errors = ref<string[]>([]);

const emit = defineEmits<{
  submitted: [];
}>();

const { handleSubmit, values } = useForm({
  validationSchema: toTypedSchema(
    z
      .object({
        descriptor: z.string().min(1, { message: "Field descriptor is required" }),
        scope: z.nativeEnum(CustomFieldScope),
        dataType: z.nativeEnum(CustomFieldDataType),
        numericFormat: z.nativeEnum(CustomFieldNumericOption).optional(),
        precision: z.number().default(0),
        listValues: z.array(z.string()).optional(),
      })
      .superRefine((val, ctx) => {
        if (val.dataType === CustomFieldDataType.LIST && !val.listValues?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["listValues"],
            message: "Must provide at least 1 list value option",
          });
        }

        if (val.dataType === CustomFieldDataType.NUMERIC && !val.numericFormat) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["numericFormat"],
            message: "Number format option is required for data type",
          });
        }
      })
  ),
  initialValues: { precision: 0 },
  keepValuesOnUnmount: true,
});

const onSubmit = handleSubmit((values: any): void => {
  let url = "/customfields/add";

  // Clear existing errors
  while (errors.value.length) {
    errors.value.pop();
  }

  axios.post(url, values).then((response) => {
    handleApiResponse(
      response,
      () => {
        emit("submitted");
        success("Custom field added!", "");
      },
      (errorlist: ApiError[]) => {
        errors.value.push("Please fix the following errors:");

        for (let error of errorlist) {
          errors.value.push(error.message);
        }
      }
    );
  });
});
</script>
